/**
 * B2BDocumentsGen.jsx — Page admin : génération des documents B2B
 *
 * Permet de créer dynamiquement un devis / contrat / facture :
 *   - Saisie des infos client (raison sociale, agrément, SIRET, email…)
 *   - Sélection des prestations depuis le catalogue (AM, A1, A2, Passerelles)
 *   - Génération côté serveur → renvoie un token partageable + un hash SHA-256
 *   - Lien partenaire (/b2b/doc/:token) à copier/envoyer pour signature électronique
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  FilePlus, FileText, FileCheck, Receipt, Trash2, Copy, ExternalLink,
  Loader2, Plus, Minus, Shield, Calendar, User, Mail, Hash, Pencil, Reply,
} from 'lucide-react';
import api, { API_URL } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const DOC_TYPE_LABEL = { devis: 'Devis', contrat: 'Contrat', facture: 'Facture' };
const DOC_TYPE_ICON  = { devis: FileText, contrat: FileCheck, facture: Receipt };

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

export default function AdminB2BDocumentsGen() {
  const [catalog, setCatalog] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [docType, setDocType] = useState('devis');
  const [client, setClient] = useState({
    raison_sociale: '', agrement: '', siret: '', nom: '', email: '', tel: '', adresse: '',
  });
  const [items, setItems] = useState([]); // [{code, qty}]
  const [creating, setCreating] = useState(false);

  // After generation
  const [generated, setGenerated] = useState(null); // {token, partner_url, doc_number, hash_sha256…}

  // Edit modal
  const [editDoc, setEditDoc] = useState(null); // le doc en cours d'édition
  const [editClient, setEditClient] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d] = await Promise.all([api.b2bDocCatalog(), api.b2bDocList()]);
      setCatalog(c.catalog || []);
      setDocs(d.items || []);
    } catch (e) {
      toast.error(e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const resetForm = () => {
    setDocType('devis');
    setClient({ raison_sociale: '', agrement: '', siret: '', nom: '', email: '', tel: '', adresse: '' });
    setItems([]);
    setGenerated(null);
  };

  const openCreate = () => { resetForm(); setCreateOpen(true); };

  const toggleItem = (code) => {
    setItems((prev) => {
      const existing = prev.find((it) => it.code === code);
      if (existing) return prev.filter((it) => it.code !== code);
      return [...prev, { code, qty: 1 }];
    });
  };

  const setQty = (code, qty) => {
    const q = Math.max(1, parseInt(qty, 10) || 1);
    setItems((prev) => prev.map((it) => (it.code === code ? { ...it, qty: q } : it)));
  };

  const totalEstim = items.reduce((acc, it) => {
    const c = catalog.find((cc) => cc.code === it.code);
    if (!c) return acc;
    return {
      net: acc.net + c.total_per_student * it.qty,
      margin: acc.margin + c.margin * it.qty,
    };
  }, { net: 0, margin: 0 });

  const canGenerate = !!client.raison_sociale.trim() && items.length > 0;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setCreating(true);
    try {
      const res = await api.b2bDocGenerate({
        doc_type: docType,
        client,
        items: items.map((it) => ({ code: it.code, qty: it.qty })),
      });
      setGenerated(res);
      toast.success(`${DOC_TYPE_LABEL[docType]} généré — ${res.doc_number}`);
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Génération échouée');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/b2b/doc/${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Lien copié'));
  };

  const openLink = (token) => {
    window.open(`${window.location.origin}/b2b/doc/${token}`, '_blank', 'noopener');
  };

  const openHtml = (token) => {
    window.open(`${API_URL}/api/b2b-docs/${token}/html`, '_blank', 'noopener');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement ce document ?')) return;
    try {
      await api.b2bDocDelete(id);
      toast.success('Document supprimé');
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Suppression impossible');
    }
  };

  const openEdit = (doc) => {
    setEditDoc(doc);
    setEditClient({ ...(doc.client || {}) });
  };

  const openReply = (doc) => {
    const sig = doc.signature || {};
    const to = sig.signer_email || doc.client?.email || '';
    if (!to) { toast.error('Aucun email destinataire trouvé'); return; }
    const docLabel = { devis: 'Devis', contrat: 'Contrat', facture: 'Facture' }[doc.doc_type] || doc.doc_type;
    const signerName = sig.signer_name || doc.client?.nom || '';
    const greet = signerName ? signerName.split(' ')[0] : '';
    const subject = `Re : ${docLabel} N° ${doc.doc_number}`;
    const body =
      `Bonjour${greet ? ' ' + greet : ''},\n\n` +
      `Je reviens vers vous concernant le ${docLabel.toLowerCase()} N° ${doc.doc_number} ` +
      `(${doc.totals?.total_net || 0}€ nets, signé le ${doc.signed_at ? new Date(doc.signed_at).toLocaleDateString('fr-FR') : ''}).\n\n` +
      `\n\n` +
      `Cordialement,\n` +
      `Bruno Llorens\n` +
      `Code 2 Roues — contact@code2roues.fr — 07 82 26 82 00\n`;
    const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  const closeEdit = () => {
    setEditDoc(null);
    setEditClient(null);
  };

  const saveEdit = async () => {
    if (!editDoc || !editClient) return;
    setEditSaving(true);
    try {
      const res = await api.b2bDocPatch(editDoc.id, { client: editClient });
      if (res?.is_orphan === false && editDoc.is_orphan) {
        toast.success('Devis lié au partenaire — plus orphelin');
      } else {
        toast.success('Devis mis à jour');
      }
      closeEdit();
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Édition impossible');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="admin-b2b-documents">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-serif font-bold" style={{fontFamily:'Fraunces, serif'}}>
            Documents B2B
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Générez devis, contrats et factures — Signature électronique eIDAS simple
          </p>
        </div>
        <Button onClick={openCreate} data-testid="btn-create-doc">
          <FilePlus className="w-4 h-4 mr-2" /> Nouveau document
        </Button>
      </div>

      {/* Documents table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : docs.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Aucun document généré. Cliquez sur « Nouveau document » pour commencer.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Type</th>
                    <th className="text-left px-3 py-2 font-semibold">N°</th>
                    <th className="text-left px-3 py-2 font-semibold">Client</th>
                    <th className="text-right px-3 py-2 font-semibold">Montant</th>
                    <th className="text-left px-3 py-2 font-semibold">Statut</th>
                    <th className="text-left px-3 py-2 font-semibold">Créé le</th>
                    <th className="text-right px-3 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => {
                    const Icon = DOC_TYPE_ICON[d.doc_type] || FileText;
                    return (
                      <tr key={d.id} className="border-t hover:bg-slate-50/50" data-testid={`doc-row-${d.id}`}>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1.5">
                            <Icon className="w-4 h-4 text-orange-500" />
                            <span className="capitalize font-medium">{DOC_TYPE_LABEL[d.doc_type] || d.doc_type}</span>
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{d.doc_number}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{d.client?.raison_sociale || '—'}</div>
                          <div className="text-xs text-slate-500">{d.client?.agrement || ''}</div>
                          {d.source === 'cal_webhook' && (
                            <div className="mt-1 flex items-center gap-1 flex-wrap">
                              <Badge variant="outline" className="border-blue-300 text-blue-700 text-[10px] py-0 px-1.5" data-testid={`cal-badge-${d.id}`}>
                                Cal.com
                              </Badge>
                              {d.is_orphan && (
                                <Badge variant="outline" className="border-amber-400 text-amber-700 text-[10px] py-0 px-1.5" data-testid={`orphan-badge-${d.id}`}>
                                  ⚠ Orphelin
                                </Badge>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold">{d.totals?.total_net || 0} €</td>
                        <td className="px-3 py-2">
                          {d.status === 'signed' ? (
                            <Badge className="bg-green-600 text-white">Signé</Badge>
                          ) : (
                            <Badge variant="outline" className="border-orange-300 text-orange-700">En attente</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">{fmtDate(d.created_at)}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openHtml(d.token)} title="Aperçu HTML" data-testid={`btn-view-${d.id}`}>
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => copyLink(d.token)} title="Copier lien" data-testid={`btn-copy-${d.id}`}>
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openLink(d.token)} title="Ouvrir lien partenaire">
                              <User className="w-4 h-4" />
                            </Button>
                            {d.status !== 'signed' && (
                              <Button size="sm" variant="ghost" onClick={() => openEdit(d)} title="Éditer client" data-testid={`btn-edit-${d.id}`}>
                                <Pencil className="w-4 h-4 text-orange-500" />
                              </Button>
                            )}
                            {d.status === 'signed' && (d.signature?.signer_email || d.client?.email) && (
                              <Button size="sm" variant="ghost" onClick={() => openReply(d)} title="Répondre au signataire" data-testid={`btn-reply-${d.id}`}>
                                <Reply className="w-4 h-4 text-blue-600" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(d.id)} title="Supprimer" data-testid={`btn-del-${d.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CREATE DIALOG */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Fraunces, serif'}}>
              {generated ? `${DOC_TYPE_LABEL[docType]} généré` : 'Nouveau document B2B'}
            </DialogTitle>
            <DialogDescription>
              {generated
                ? 'Partagez le lien ci-dessous avec le partenaire pour signature électronique.'
                : 'Choisissez le type, le client et les prestations — génération immédiate avec hash SHA-256.'}
            </DialogDescription>
          </DialogHeader>

          {generated ? (
            // ---------- Success view ----------
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 font-semibold text-green-800">
                  <FileCheck className="w-5 h-5" />
                  Document généré — {generated.doc_number}
                </div>
                <div className="text-xs text-green-700 mt-1 flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  {generated.hash_sha256?.slice(0, 32)}… (SHA-256)
                </div>
              </div>

              <div className="space-y-2">
                <Label>Lien partenaire (signature électronique)</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}${generated.partner_url}`}
                    className="font-mono text-xs"
                    data-testid="generated-partner-url"
                  />
                  <Button onClick={() => copyLink(generated.token)} data-testid="btn-copy-partner-url">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" onClick={() => openLink(generated.token)}>
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm pt-2">
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-xs text-slate-500">Total net</div>
                  <div className="font-bold">{generated.totals?.total_net || 0} €</div>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-xs text-slate-500">Marge estimée</div>
                  <div className="font-bold text-green-600">{generated.totals?.total_margin || 0} €</div>
                </div>
              </div>

              <DialogFooter className="pt-2">
                <Button variant="outline" onClick={() => { setCreateOpen(false); resetForm(); }}>Fermer</Button>
                <Button onClick={() => { resetForm(); }} data-testid="btn-new-after">
                  <FilePlus className="w-4 h-4 mr-2" /> Générer un autre
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // ---------- Create form ----------
            <div className="space-y-4">
              {/* Type */}
              <div>
                <Label className="mb-2 block">Type de document</Label>
                <div className="grid grid-cols-3 gap-2">
                  {['devis', 'contrat', 'facture'].map((t) => {
                    const Icon = DOC_TYPE_ICON[t];
                    const active = docType === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setDocType(t)}
                        data-testid={`btn-type-${t}`}
                        className={`flex items-center justify-center gap-2 border rounded-lg p-3 text-sm transition ${
                          active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50 border-slate-200'
                        }`}
                      >
                        <Icon className="w-4 h-4" /> {DOC_TYPE_LABEL[t]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Client */}
              <div>
                <Label className="mb-2 block">Informations client</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input placeholder="Raison sociale *" value={client.raison_sociale} onChange={(e)=>setClient({...client, raison_sociale:e.target.value})} data-testid="input-raison-sociale" />
                  <Input placeholder="Agrément" value={client.agrement} onChange={(e)=>setClient({...client, agrement:e.target.value})} data-testid="input-agrement" />
                  <Input placeholder="SIRET" value={client.siret} onChange={(e)=>setClient({...client, siret:e.target.value})} />
                  <Input placeholder="Nom représentant" value={client.nom} onChange={(e)=>setClient({...client, nom:e.target.value})} />
                  <Input placeholder="Email" type="email" value={client.email} onChange={(e)=>setClient({...client, email:e.target.value})} />
                  <Input placeholder="Téléphone" value={client.tel} onChange={(e)=>setClient({...client, tel:e.target.value})} />
                  <Input placeholder="Adresse complète" className="md:col-span-2" value={client.adresse} onChange={(e)=>setClient({...client, adresse:e.target.value})} />
                </div>
              </div>

              {/* Items */}
              <div>
                <Label className="mb-2 block">Prestations</Label>
                <div className="space-y-2">
                  {catalog.map((c) => {
                    const selected = items.find((it) => it.code === c.code);
                    return (
                      <div
                        key={c.code}
                        className={`border rounded-lg p-3 transition ${selected ? 'border-orange-400 bg-orange-50/60' : 'border-slate-200'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <label className="flex-1 flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={!!selected}
                              onChange={() => toggleItem(c.code)}
                              data-testid={`chk-item-${c.code}`}
                            />
                            <div className="flex-1">
                              <div className="font-medium text-sm">{c.label}</div>
                              <div className="text-xs text-slate-500 mt-0.5">
                                {c.duration} • {c.unit_price_net} €/h = {c.total_per_student} € net • marge {c.margin} €
                              </div>
                            </div>
                          </label>
                          {selected && (
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="outline" onClick={() => setQty(c.code, selected.qty - 1)} disabled={selected.qty<=1}>
                                <Minus className="w-3 h-3" />
                              </Button>
                              <Input
                                type="number"
                                min={1}
                                value={selected.qty}
                                onChange={(e) => setQty(c.code, e.target.value)}
                                className="w-16 text-center h-8"
                                data-testid={`qty-${c.code}`}
                              />
                              <Button size="sm" variant="outline" onClick={() => setQty(c.code, selected.qty + 1)}>
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Totals preview */}
              {items.length > 0 && (
                <div className="grid grid-cols-2 gap-3 bg-slate-900 text-white rounded-lg p-3">
                  <div>
                    <div className="text-xs text-slate-400">Total net estimé</div>
                    <div className="text-lg font-bold" data-testid="preview-total-net">{totalEstim.net} €</div>
                  </div>
                  <div>
                    <div className="text-xs text-green-400">Marge estimée</div>
                    <div className="text-lg font-bold text-green-400" data-testid="preview-total-margin">{totalEstim.margin} €</div>
                  </div>
                </div>
              )}

              <DialogFooter className="pt-2">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
                <Button onClick={handleGenerate} disabled={!canGenerate || creating} data-testid="btn-generate">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FilePlus className="w-4 h-4 mr-2" />}
                  Générer le {DOC_TYPE_LABEL[docType].toLowerCase()}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* EDIT CLIENT DIALOG */}
      <Dialog open={!!editDoc} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="max-w-xl">
          {editDoc && editClient && (
            <>
              <DialogHeader>
                <DialogTitle style={{fontFamily:'Fraunces, serif'}}>
                  Éditer le client — {editDoc.doc_number}
                </DialogTitle>
                <DialogDescription>
                  Renseignez les informations du partenaire. Le hash du document sera renouvelé automatiquement.
                  {editDoc.is_orphan && (
                    <span className="block mt-1 text-amber-600 text-xs">
                      ⚠ Ce devis est actuellement orphelin — complétez raison sociale + agrément pour le lier.
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="md:col-span-2">
                  <Label>Raison sociale *</Label>
                  <Input
                    value={editClient.raison_sociale || ''}
                    onChange={(e) => setEditClient({...editClient, raison_sociale: e.target.value})}
                    placeholder="Auto-école Martin SAS"
                    data-testid="edit-raison-sociale"
                  />
                </div>
                <div>
                  <Label>Agrément *</Label>
                  <Input
                    value={editClient.agrement || ''}
                    onChange={(e) => setEditClient({...editClient, agrement: e.target.value})}
                    placeholder="E 25 038 0009 1"
                    data-testid="edit-agrement"
                  />
                </div>
                <div>
                  <Label>SIRET</Label>
                  <Input
                    value={editClient.siret || ''}
                    onChange={(e) => setEditClient({...editClient, siret: e.target.value})}
                    placeholder="82345678900012"
                  />
                </div>
                <div>
                  <Label>Nom du représentant</Label>
                  <Input
                    value={editClient.nom || ''}
                    onChange={(e) => setEditClient({...editClient, nom: e.target.value})}
                    placeholder="DUPONT Jean"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editClient.email || ''}
                    onChange={(e) => setEditClient({...editClient, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    value={editClient.tel || ''}
                    onChange={(e) => setEditClient({...editClient, tel: e.target.value})}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Adresse</Label>
                  <Input
                    value={editClient.adresse || ''}
                    onChange={(e) => setEditClient({...editClient, adresse: e.target.value})}
                    placeholder="12 avenue du Lyon — 38300 Saint-Savin"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closeEdit}>Annuler</Button>
                <Button
                  onClick={saveEdit}
                  disabled={!editClient.raison_sociale?.trim() || editSaving}
                  data-testid="btn-save-edit"
                >
                  {editSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pencil className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
